import { Controller } from "@hotwired/stimulus"

/**
 * Contact Picker Controller - Opens a modal with phone contacts for selection
 * Works with Flutter WebView handler for mobile app
 */
export default class extends Controller {
  static targets = [
    "nameField", 
    "phoneField", 
    "countryCodeField", 
    "selectButton", 
    "statusMessage",
    "modal",
    "contactsList",
    "searchInput",
    "loadingState",
    "emptyState",
    "permissionState"
  ]

  connect() {
    // Check if we're in the mobile app (Flutter WebView)
    this.isInApp = typeof window.flutter_inappwebview !== "undefined"
    this.contacts = []
    this.filteredContacts = []
    
    // Hide button if not in mobile app
    if (!this.isInApp && this.hasSelectButtonTarget) {
      this.selectButtonTarget.style.display = "none"
    }
  }

  async openContactPicker(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.isInApp) {
      this.showMessage("Cette fonctionnalité n'est disponible que dans l'application mobile INNTRO")
      return
    }

    // Show modal
    this.showModal()
    this.showLoading()

    try {
      // Request contacts from Flutter
      const result = await this.getContactsFromDevice()

      if (!result.success) {
        if (result.permissionDenied) {
          this.showPermissionRequest()
        } else {
          this.showError(result.error || "Erreur lors du chargement des contacts")
        }
        return
      }

      if (!result.contacts || result.contacts.length === 0) {
        this.showEmpty("Aucun contact avec numéro de téléphone trouvé")
        return
      }

      // Store contacts and display them
      this.contacts = result.contacts.map(c => ({
        name: c.name || c.displayName || "Sans nom",
        phone: c.phone || (c.phones && c.phones[0]) || ""
      })).filter(c => c.phone) // Only keep contacts with phone numbers

      this.filteredContacts = [...this.contacts]
      this.renderContacts()

    } catch (error) {
      console.error("Contact picker error:", error)
      this.showError("Impossible d'accéder aux contacts")
    }
  }

  /**
   * Get contacts from device using Flutter handler
   * Uses the same handler as contacts_import_controller.js
   */
  async getContactsFromDevice() {
    try {
      // Use importContacts which is the working Flutter handler
      const result = await window.flutter_inappwebview.callHandler("importContacts")
      
      console.log("importContacts raw result:", JSON.stringify(result))
      
      // Normalize the result structure based on different response formats
      let contacts = []
      
      if (!result) {
        return { success: false, error: "Aucune réponse du téléphone", permissionDenied: false }
      }
      
      // Handle explicit error response
      if (result.success === false) {
        return {
          success: false,
          error: result.error || "Erreur lors de l'accès aux contacts",
          permissionDenied: result.permissionDenied || result.permission_denied || false
        }
      }
      
      // Extract contacts from various response formats
      if (result.contacts && Array.isArray(result.contacts)) {
        contacts = result.contacts
      } else if (Array.isArray(result)) {
        contacts = result
      } else if (result.data && Array.isArray(result.data)) {
        contacts = result.data
      } else if (typeof result === "object") {
        // Result might be a raw object with contact data or indexed object
        const values = Object.values(result).filter(item => 
          item && typeof item === "object" && (item.phone || item.phones || item.phoneNumbers || item.displayName || item.name)
        )
        if (values.length > 0) {
          contacts = values
        }
      }
      
      console.log("Extracted contacts before normalization:", contacts.length)
      
      // Filter and normalize contacts
      const normalizedContacts = []
      for (const c of contacts) {
        let phone = ""
        
        // Try different phone field formats
        if (typeof c.phone === "string" && c.phone.trim()) {
          phone = c.phone.trim()
        } else if (c.phones && Array.isArray(c.phones) && c.phones.length > 0) {
          const firstPhone = c.phones[0]
          if (typeof firstPhone === "string") {
            phone = firstPhone
          } else if (firstPhone && typeof firstPhone === "object") {
            phone = firstPhone.number || firstPhone.value || firstPhone.phone || ""
          }
        } else if (c.phoneNumbers && Array.isArray(c.phoneNumbers) && c.phoneNumbers.length > 0) {
          const firstPhone = c.phoneNumbers[0]
          if (typeof firstPhone === "string") {
            phone = firstPhone
          } else if (firstPhone && typeof firstPhone === "object") {
            phone = firstPhone.number || firstPhone.value || firstPhone.phone || ""
          }
        }
        
        // Ensure phone is a string
        if (typeof phone !== "string") {
          phone = String(phone || "")
        }
        phone = phone.trim()
        
        // Get name from various fields
        const name = (c.name || c.displayName || c.givenName || c.familyName || "").trim() || "Sans nom"
        
        if (phone.length > 0) {
          normalizedContacts.push({ name, phone })
        }
      }
      
      console.log("Normalized contacts:", normalizedContacts.length)
      
      return { success: true, contacts: normalizedContacts }
    } catch (error) {
      console.error("Error calling importContacts:", error)
      return { 
        success: false, 
        error: "Impossible d'accéder aux contacts de l'appareil: " + error.message, 
        permissionDenied: false 
      }
    }
  }

  /**
   * Request permission from Flutter
   */
  async requestPermission() {
    try {
      this.showLoading()
      const result = await window.flutter_inappwebview.callHandler("requestContactsPermission")
      
      if (result.granted) {
        // Try to get contacts again
        const contactsResult = await this.getContactsFromDevice()
        if (contactsResult.success && contactsResult.contacts) {
          this.contacts = contactsResult.contacts.map(c => ({
            name: c.name || c.displayName || "Sans nom",
            phone: c.phone || (c.phones && c.phones[0]) || ""
          })).filter(c => c.phone)
          this.filteredContacts = [...this.contacts]
          this.renderContacts()
        } else {
          this.showEmpty()
        }
      } else {
        this.showPermissionRequest()
      }
    } catch (error) {
      console.error("Permission request error:", error)
      this.showError("Impossible de demander la permission")
    }
  }

  /**
   * Filter contacts based on search input
   */
  searchContacts() {
    const query = this.searchInputTarget.value.toLowerCase().trim()
    
    if (query === "") {
      this.filteredContacts = [...this.contacts]
    } else {
      this.filteredContacts = this.contacts.filter(contact => 
        contact.name.toLowerCase().includes(query) ||
        contact.phone.includes(query)
      )
    }
    
    this.renderContacts()
  }

  /**
   * Render filtered contacts in the list
   */
  renderContacts() {
    this.hideAllStates()
    
    if (this.filteredContacts.length === 0) {
      if (this.contacts.length > 0) {
        this.showEmpty("Aucun contact ne correspond à votre recherche")
      } else {
        this.showEmpty("Aucun contact avec numéro de téléphone trouvé")
      }
      return
    }

    const html = this.filteredContacts.map((contact, index) => `
      <button type="button" 
              class="w-full flex items-center gap-3 p-3 hover:bg-slate-700/50 rounded-lg transition-colors text-left"
              data-action="click->contact-picker#selectContact"
              data-contact-index="${index}">
        <div class="flex-shrink-0 size-10 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-white font-semibold text-sm">
          ${this.getInitials(contact.name)}
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-slate-100 truncate">${this.escapeHtml(contact.name)}</p>
          <p class="text-xs text-slate-400 truncate">${this.escapeHtml(contact.phone)}</p>
        </div>
        <svg class="size-5 text-slate-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    `).join("")

    this.contactsListTarget.innerHTML = html
    this.contactsListTarget.classList.remove("hidden")
  }

  /**
   * Handle contact selection
   */
  selectContact(event) {
    const index = parseInt(event.currentTarget.dataset.contactIndex)
    const contact = this.filteredContacts[index]

    if (!contact) return

    // Update name field
    if (this.hasNameFieldTarget) {
      this.nameFieldTarget.value = contact.name
      this.nameFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    // Parse and update phone number
    const { countryCode, localNumber } = this.parsePhoneNumber(contact.phone)

    if (this.hasCountryCodeFieldTarget && countryCode) {
      // Try to update country-selector controller if the field is a hidden input
      const wrapper = this.countryCodeFieldTarget.closest("[data-controller='country-selector']")
      if (wrapper) {
        // Get the country-selector controller instance
        const selectorController = this.application.getControllerForElementAndIdentifier(
          wrapper,
          "country-selector"
        )
        if (selectorController && typeof selectorController.fetchAndApplyCountryByCode === "function") {
          selectorController.fetchAndApplyCountryByCode(countryCode)
        }
      } else {
        // Fallback for select elements
        if (this.countryCodeFieldTarget.tagName === "SELECT") {
          const options = Array.from(this.countryCodeFieldTarget.options)
          const matchingOption = options.find(opt => opt.value === countryCode)
          if (matchingOption) {
            this.countryCodeFieldTarget.value = countryCode
          }
        } else {
          // Regular input field
          this.countryCodeFieldTarget.value = countryCode
        }
        this.countryCodeFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    if (this.hasPhoneFieldTarget) {
      this.phoneFieldTarget.value = localNumber
      this.phoneFieldTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    // Close modal
    this.closeModal()
  }

  /**
   * Parse phone number to extract country code and local number
   */
  parsePhoneNumber(phoneNumber) {
    const cleaned = phoneNumber.replace(/[^\d+]/g, "")

    const countryCodes = [
      "+33", "+1", "+44", "+49", "+39", "+34", "+32", "+41",
      "+212", "+237", "+225", "+221", "+243", "+228", "+229"
    ]

    let countryCode = null
    let localNumber = cleaned

    // Try to match country codes with +
    for (const code of countryCodes) {
      if (cleaned.startsWith(code)) {
        countryCode = code
        localNumber = cleaned.substring(code.length)
        break
      }
    }

    // Try without + prefix
    if (!countryCode && cleaned.length > 10) {
      const codesWithoutPlus = countryCodes.map(c => c.substring(1))
      for (let i = 0; i < codesWithoutPlus.length; i++) {
        if (cleaned.startsWith(codesWithoutPlus[i])) {
          countryCode = countryCodes[i]
          localNumber = cleaned.substring(codesWithoutPlus[i].length)
          break
        }
      }
    }

    // Default to +33 if starts with 0
    if (!countryCode && cleaned.startsWith("0")) {
      countryCode = "+33"
      localNumber = cleaned.substring(1)
    }

    // Remove leading zeros
    localNumber = localNumber.replace(/^0+/, "")

    return { countryCode: countryCode || "+33", localNumber }
  }

  /**
   * Modal management
   */
  showModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      this.modalTarget.classList.add("flex")
      document.body.style.overflow = "hidden"
    }
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this.modalTarget.classList.remove("flex")
      document.body.style.overflow = ""
    }
    // Clear search
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
    }
  }

  /**
   * State management
   */
  hideAllStates() {
    if (this.hasLoadingStateTarget) this.loadingStateTarget.classList.add("hidden")
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    if (this.hasPermissionStateTarget) this.permissionStateTarget.classList.add("hidden")
    if (this.hasContactsListTarget) this.contactsListTarget.classList.add("hidden")
  }

  showLoading() {
    this.hideAllStates()
    if (this.hasLoadingStateTarget) {
      this.loadingStateTarget.classList.remove("hidden")
    }
  }

  showEmpty(message = null) {
    this.hideAllStates()
    if (this.hasEmptyStateTarget) {
      if (message) {
        this.emptyStateTarget.innerHTML = `
          <div class="py-8 text-center">
            <svg class="mx-auto size-12 text-slate-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="11" x2="23" y2="11"/>
            </svg>
            <p class="mt-2 text-sm text-slate-400">${this.escapeHtml(message)}</p>
          </div>
        `
      }
      this.emptyStateTarget.classList.remove("hidden")
    }
  }

  showPermissionRequest() {
    this.hideAllStates()
    if (this.hasPermissionStateTarget) {
      this.permissionStateTarget.classList.remove("hidden")
    }
  }

  showError(message) {
    this.hideAllStates()
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.innerHTML = `
        <div class="text-center py-8">
          <svg class="mx-auto size-12 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <p class="mt-2 text-sm text-red-400">${this.escapeHtml(message)}</p>
        </div>
      `
      this.emptyStateTarget.classList.remove("hidden")
    }
  }

  showMessage(message) {
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = message
      this.statusMessageTarget.classList.remove("hidden")
      setTimeout(() => {
        this.statusMessageTarget.classList.add("hidden")
      }, 3000)
    } else {
      alert(message)
    }
  }

  /**
   * Utility functions
   */
  getInitials(name) {
    return name
      .split(" ")
      .slice(0, 2)
      .map(word => word.charAt(0).toUpperCase())
      .join("")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
